import { Component, OnInit, OnDestroy, NgZone, DestroyRef, inject } from '@angular/core';
import { Hub } from 'aws-amplify/utils';
import { fetchAuthSession, signInWithRedirect, signOut } from "aws-amplify/auth";
import { Router, ActivatedRoute } from '@angular/router';
import { UserService } from '@shared/services/user/user.service';
import { loginSuccess } from '@app/auth/store/auth.actions';
import { Store } from '@ngrx/store';
import { CustomSnackbarService } from '@shared/components/custom-snackbar/custom-snackbar.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface HubEvent {
  payload: {
    event: 'signedIn' | 'signIn_failure';
    data?: any;
  };
}

@Component({
    selector: 'app-loginsso',
    templateUrl: './loginsso.component.html',
    styleUrls: ['./loginsso.component.scss'],
    standalone: false
})
export class LoginssoComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);

  private hubListenerCancel: () => void;

  constructor(
    private customSnackbarService: CustomSnackbarService,
    private store: Store,
    private userService: UserService,
    private router: Router,
    private ngZone: NgZone,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.setupHubListener();
    this.setupRouteSubscription();
  }

  ngOnDestroy(): void {
    if (this.hubListenerCancel) {
      this.hubListenerCancel();
    }
  }

  private setupHubListener(): void {
    const listener = async (data: HubEvent) => {
      switch (data.payload.event) {
        case 'signedIn':
          try {
            const cognitoTokens = (await fetchAuthSession()).tokens;
            const rawToken = cognitoTokens?.idToken?.toString();

            if (!rawToken) {
              throw new Error('No ID token received');
            }

            this.ngZone.run(() => this.login(rawToken));
          } catch (error) {
            this.handleError('Failed to process sign in', error);
          }
          break;
        case 'signIn_failure':
          this.customSnackbarService.showSnackbar('Sign in failed. Please try again.', 'warning');
          break;
      }
    };

    this.hubListenerCancel = Hub.listen('auth', listener);
  }

  private setupRouteSubscription(): void {
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        const code = 'code' in params;
        const error = params['error_description'];

        if (error) {
          this.customSnackbarService.showSnackbar(params['error_description'], 'warning');
        } else if (!code) {
          this.handleSSO();
        }
      });
  }

  private async handleSSO(): Promise<void> {
    try {
      await signInWithRedirect();
    } catch (error) {
      this.handleError('Failed to initiate SSO', error);
      await signOut();
    }
  }

  private login(id_token: string): void {
    this.userService.login({ id_token }, 'sso-login/')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          // Use structuredClone for deep copy if available, fallback to JSON
          const deepCopyData = typeof structuredClone === 'function'
            ? structuredClone(res)
            : JSON.parse(JSON.stringify(res));

          this.store.dispatch(loginSuccess({ user: deepCopyData }));
          this.router.navigate(['/']);
        },
        error: (error) => {
          this.handleError('Login failed', error);
        }
      });
  }

  private handleError(message: string, error: any): void {
    console.error(message, error);
    this.customSnackbarService.showSnackbar(message, 'warning');
  }
}
